import { create } from 'zustand';
import { TenantService } from 'src/services/shared/tenant.service';
import {
  TenantContext,
  TenantType,
  TenantRole,
  TenantMember,
  Group,
} from 'src/models/Tenant';

// Types
interface TenantState {
  // Tenant context (loaded once after login)
  context: TenantContext | null;
  isLoadingContext: boolean;

  // Groups (admin-managed)
  groups: Group[];
  isLoadingGroups: boolean;

  // Tenant members (admin-managed)
  members: TenantMember[];
  isLoadingMembers: boolean;

  // Error handling
  error: string | null;

  // Computed helpers
  isAdmin: boolean;
  isMultiUser: boolean;
  tenantType: TenantType | null;

  // Actions
  loadTenantContext: () => Promise<void>;
  loadGroups: () => Promise<void>;
  loadMembers: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

// Initial state
const initialState = {
  context: null,
  isLoadingContext: false,
  groups: [],
  isLoadingGroups: false,
  members: [],
  isLoadingMembers: false,
  error: null,
  isAdmin: false,
  isMultiUser: false,
  tenantType: null as TenantType | null,
};

// Create the store
export const useTenantStore = create<TenantState>((set, get) => ({
  ...initialState,

  loadTenantContext: async () => {
    try {
      set({ isLoadingContext: true, error: null });

      const context = await TenantService.getTenantContext();

      set({
        context,
        isAdmin: context.role === 'admin',
        isMultiUser: context.tenantType !== 'individual',
        tenantType: context.tenantType,
        isLoadingContext: false,
      });
    } catch (error: any) {
      const message = error.message || 'Failed to load tenant context';
      console.error('CRITICAL: Failed to load tenant context:', error);
      set({
        error: message,
        isLoadingContext: false,
      });
    }
  },

  loadGroups: async () => {
    try {
      set({ isLoadingGroups: true, error: null });

      const groups = await TenantService.getGroups();

      set({ groups, isLoadingGroups: false });
    } catch (error: any) {
      console.error('Failed to load groups:', error);
      set({
        error: error.message || 'Failed to load groups',
        isLoadingGroups: false,
      });
    }
  },

  loadMembers: async () => {
    try {
      set({ isLoadingMembers: true, error: null });

      const members = await TenantService.getTenantMembers();

      set({ members, isLoadingMembers: false });
    } catch (error: any) {
      console.error('Failed to load tenant members:', error);
      set({
        error: error.message || 'Failed to load tenant members',
        isLoadingMembers: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    TenantService.clearCache();
    set(initialState);
  },
}));

// Selectors
export const useTenantContext = () => useTenantStore(state => state.context);
export const useTenantRole = () => useTenantStore(state => state.context?.role as TenantRole | undefined);
export const useIsAdmin = () => useTenantStore(state => state.isAdmin);
export const useIsMultiUser = () => useTenantStore(state => state.isMultiUser);
export const useTenantType = () => useTenantStore(state => state.tenantType);
export const useTenantGroups = () => useTenantStore(state => state.groups);
export const useTenantMembers = () => useTenantStore(state => state.members);
export const useTenantLoading = () => useTenantStore(state => state.isLoadingContext);
export const useTenantError = () => useTenantStore(state => state.error);

export default useTenantStore;
